<script>
import { mapActions, mapGetters, mapState } from "vuex";

import vDatePicker from "v-calendar/lib/components/date-picker.umd";
import HourPicker from "@/components/_Shared/HourPicker";
import Tip from "@/components/_base/Tip";

import moment from "@/utils/moment";

import orderSchedulerHelper from "@/utils/helpers/orderSchedulerHelper";
import specialDatesHelper from "@/utils/helpers/specialDatesHelper";

export default {
  components: {
    vDatePicker,
    HourPicker,
    Tip
  },

  methods: {
    ...mapActions("orderCancellation", ["updateReschedule"]),

    handleDateChange(day) {
      if (day.isDisabled) {
        return;
      }

      this.updateReschedule({ date: day.date });
    }
  },

  computed: {
    ...mapState("orderCancellation", ["order", "reschedule"]),
    ...mapGetters("orderCancellation", ["allowToSendTicket", "isShortNotice"]),

    fromDate: function() {
      return orderSchedulerHelper.fromDate();
    },

    toDate: function() {
      return orderSchedulerHelper.toDate();
    },

    disabledDates: function() {
      return specialDatesHelper.daysWithDisabledPurchase();
    },

    startsAt: function() {
      return moment(this.order.schedule_event.starts_at);
    }
  },

  filters: {
    startingAt(date, initialHour = null) {
      let formatedDate = moment(date);

      if (initialHour) {
        formatedDate = formatedDate.startOf("day").add(initialHour, "hours");
      }

      return formatedDate.format("DD/MM/Y [às] HH[h]");
    },

    rescheduleOption(type) {
      if (type === "ANY") {
        return "Não tenho preferência por profissional autônomo específico.";
      }

      if (type === "KEEP") {
        return "Prefiro tentar reagendar com o mesmo profissional autônomo que agendei.";
      }

      if (type === "CHOOSE") {
        return "Gostaria de escolher um novo profissional autônomo.";
      }

      return "Selecione uma das opções acima antes de continuar.";
    }
  },

  mounted() {
    this.updateReschedule({ initialHour: this.startsAt.hour() });
  }
};
</script>

<template>
  <div class="root">
    <div class="date-time">
      <div class="date">
        <p>Nova data para a limpeza:</p>

        <v-date-picker
          color="purple"
          :masks="{ weekdays: 'WWW' }"
          locale="pt-BR"
          :value="reschedule.date"
          :input-props="{
            class: 'w-full shadow appearance-none border rounded py-2 px-3 text-gray-700 hover:border-blue-5 text-center',
            placeholder: 'Selecione um dia',
            readonly: true
          }"
          :first-day-of-week='1'
          :min-date="fromDate"
          :max-date="toDate"
          :disabled-dates="disabledDates"
          :popover="{ placement: $screens({ default: 'bottom'}) }"
          @dayclick="handleDateChange"
        />
      </div>

      <div class="time">
        <p>Novo horário de início da limpeza:</p>

        <HourPicker
          :hour="this.reschedule.initialHour"
          :startsAt="reschedule.date || this.startsAt"
          @change="(hour) => updateReschedule({ initialHour: hour })"
        />
      </div>
    </div>

    <div class="professional">
      <p>Sobre o profissional, você:</p>

      <div class="radio-group">
        <div class="radio">
          <input
            id="keep"
            type="radio"
            name="keep"
            :value="'KEEP'"
            :checked="reschedule.type === 'KEEP'"
            @change="updateReschedule({ type: 'KEEP' })"
          />

          <label for="keep">
            <p>Prefiro tentar reagendar com o mesmo profissional autônomo que agendei</p>
          </label>
        </div>

        <div class="radio">
          <input
            id="any"
            type="radio"
            name="any"
            :value="'ANY'"
            :checked="reschedule.type === 'ANY'"
            @change="updateReschedule({ type: 'ANY' })"
          />

          <label for="any">
            <p>Não tenho preferência por profissional autônomo específico</p>
          </label>
        </div>

        <div class="radio">
          <input
            id="choose"
            type="radio"
            name="choose"
            :value="'CHOOSE'"
            :checked="reschedule.type === 'CHOOSE'"
            @change="updateReschedule({ type: 'CHOOSE' })"
          />

          <label for="choose">
            <p>Gostaria de escolher um novo profissional autônomo</p>
          </label>
        </div>
      </div>
    </div>

    <div class="review" v-if="allowToSendTicket">
      <p>Resumo do reagendamento:</p>

      <div>
        <label for="from">Reagendar a limpeza do dia:</label>
        <span id="from">
          {{ order.schedule_event.starts_at | startingAt }}
        </span>
      </div>

      <div>
        <label for="to">Para o dia:</label>
        <span id="to">
          {{ reschedule.date | startingAt(reschedule.initialHour) }}
        </span>
      </div>

      <div>
        <span id="from">
          {{ reschedule.type | rescheduleOption }}
        </span>
      </div>
    </div>

    <div class="alert" v-if="isShortNotice && this.order.status != 'service_not_provided'">
      <Tip type="alert">
        Atenção, cancelamento entre 24h e 3h o cliente perde 1h para que a profissional seja compensada pelo dia de trabalho ocupado.
      </Tip>

      <router-link :to="{name: 'help'}">
        Veja as regras de reagendamento aqui
    </router-link>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.date,
.time {
  display: flex;
  flex-direction: column;
  align-items: center;
}

p {
  text-align: center;
}

.date >>> .vc-title {
  font-weight: 400;
  text-transform: capitalize;
}

.date >>> .vc-weekday {
  font-weight: 400;
  text-transform: capitalize;
  font-size: 12px;
}

.date >>> .vc-day-content {
  font-weight: 400;
  text-transform: capitalize;
}

.date >>> .vc-bg-purple-600 {
  background-color: #603285;
}

.professional {
  margin-top: 24px;
}

.radio-group {
  margin-top: 12px;

  > .radio {
    display: flex;
    align-items: center;

    > input {
      margin: 0;
      margin-right: 12px;

      &:checked ~ label {
        color: $purple;
        opacity: 1;
        font-weight: 400;
      }
    }

    > label {
      font-size: 14px;
      opacity: 0.75;

      > p {
        margin: 0;
        text-align: start;
      }
    }
  }

  > *:not(:first-child) {
    margin-top: 12px;
  }

  @media (min-width: 768px) {
    > *:not(:first-child) {
      margin-top: 0;
    }
  }
}

.review {
  margin-top: 24px;
  color: #424242;

  > *:not(:first-child) {
    margin-top: 12px;
  }
  div {
    font-size: 14px;

    span {
      font-weight: 500;
    }
  }
}

.alert {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.alert > a {
  margin-top: 4px;
  color: #cfb2c4;
  text-decoration: underline;
  font-weight: 400;
}
</style>



// WEBPACK FOOTER //
// src/components/Order/Components/Actions/Cancellation/CancellationReschedule.vue